/**
 * /publisher — the Publisher's front door.
 *
 * Mirrors StudioEntry's one job: decide whether entering the product should resume active
 * work or show the area's home screen. The home screen itself stays Publisher.tsx; this
 * route is only the decision in front of it.
 *
 * The explicit `?dashboard=1` path is the escape hatch that keeps the user from being
 * trapped in auto-resume. Plain `/publisher` means "take me back to my work" when a draft
 * is in flight; `/publisher?dashboard=1` means "show me the dashboard even if something is
 * resumable".
 */
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePublisher } from '../hooks';
import Publisher from './Publisher';
import { paths } from './paths';

export default function PublisherEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { lastVisibleSurface, mostRecentDraftProgress } = usePublisher();
  const dashboardOnly = searchParams.get('dashboard') === '1';
  const resumedRef = useRef(false);
  const resumeTarget = useMemo(() => {
    if (dashboardOnly) return null;
    if (lastVisibleSurface?.kind === 'wizard') {
      return lastVisibleSurface.releaseId
        ? `${paths.publisherNewRelease}?release=${lastVisibleSurface.releaseId}`
        : paths.publisherNewRelease;
    }
    if (lastVisibleSurface?.kind === 'detail') return paths.publisherRelease(lastVisibleSurface.releaseId);
    if (lastVisibleSurface?.kind === 'dashboard') return null;
    return mostRecentDraftProgress?.[0]
      ? `${paths.publisherNewRelease}?release=${mostRecentDraftProgress[0]}`
      : null;
  }, [dashboardOnly, lastVisibleSurface, mostRecentDraftProgress]);

  useEffect(() => {
    if (!resumeTarget || resumedRef.current) return;
    resumedRef.current = true;
    navigate(resumeTarget, { replace: true });
  }, [navigate, resumeTarget]);

  if (resumeTarget) return null;
  return <Publisher />;
}
